import React from 'react'
import { ChatterMessage as ChatterMessageType } from '../services/chatterService'

interface ChatterMessageProps {
  message: ChatterMessageType
  isCurrentUser: boolean
}

const ChatterMessage: React.FC<ChatterMessageProps> = ({ message, isCurrentUser }) => {
  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    }
  }

  const getMessageTypeIcon = () => {
    switch (message.messageType) {
      case 'file_request':
        return '📎'
      case 'file_upload':
        return '📤'
      default:
        return null
    }
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return '📄'
    if (mimeType.includes('image')) return '🖼️'
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝'
    return '📎'
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className={`chatter-message ${isCurrentUser ? 'message-sent' : 'message-received'}`}>
      <div className="message-header">
        <span className="sender-name">
          {isCurrentUser ? 'You' : message.senderName || 'Unknown'}
        </span>
        <span className="message-time">{formatTime(message.createdAt)}</span>
        {getMessageTypeIcon() && (
          <span className="message-type-icon">{getMessageTypeIcon()}</span>
        )}
      </div>
      
      <div className="message-content">
        <p className="message-text">{message.messageText}</p>
        
        {message.fileAttachments && message.fileAttachments.length > 0 && (
          <div className="message-attachments">
            {message.fileAttachments.map((file, index) => (
              <div key={index} className="attachment-item">
                <span className="file-icon">{getFileIcon(file.mimeType)}</span>
                <div className="file-info">
                  <span className="file-name">{file.originalName}</span>
                  <span className="file-size">{formatFileSize(file.size)}</span>
                </div>
                {file.url && (
                  <a 
                    href={file.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="file-download"
                  >
                    ⬇️
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {!message.isRead && !isCurrentUser && (
        <div className="unread-indicator">●</div>
      )}
    </div>
  )
}

export default ChatterMessage
